<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import PageHeader from '@/V2/components/PageHeader.vue';
import {
  iButton,
  iIcon,
  iSpinner,
  iTextInput,
  iTextInputModes,
  showToast,
} from '@inscribe/signal';
import { useSettingsStore } from '@/V2/stores/pinia/settings.store';
const settingsStore = useSettingsStore();

const memory = ref('');
const isSaving = ref(false);

const cachedMemory = computed(
  () => settingsStore.getChatAgentWorkspaceMemory ?? '',
);
const error = computed(() => settingsStore.getChatAgentWorkspaceMemoryError);
const hasNeverLoaded = computed(
  () => settingsStore.getChatAgentWorkspaceMemory === undefined,
);
const isLoading = computed(() => hasNeverLoaded.value && !error.value);
const hasChanges = computed(() => memory.value !== cachedMemory.value);

watch(
  cachedMemory,
  (val) => {
    memory.value = val;
  },
  { immediate: true },
);

onMounted(() => {
  settingsStore.fetchChatAgentWorkspaceMemory();
});

async function save() {
  isSaving.value = true;
  try {
    await settingsStore.updateChatAgentWorkspaceMemory(memory.value);
    showToast({ text: 'Assistant memory updated' });
  } catch (error: any) {
    showToast({
      text:
        error?.response?.data?.message || 'Failed to update Assistant memory',
      isError: true,
    });
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <div class="settings-page d-flex flex-column">
    <PageHeader title="Assistant" />
    <div
      class="settings-page__body d-flex flex-column align-items-center i-px-12 i-py-11"
    >
      <div class="d-flex justify-content-center w-100">
        <div class="settings-page-view">
          <div class="settings-page-block i-pb-14">
            <div class="settings-page-block-title">
              <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-0">Memory</h5>
              <p class="i-text-body-2 i-text-neutral-110">
                Customize what the Assistant remembers about you and your
                workflow. Share context like your name, preferences, custom
                rules, or time-sensitive information such as ongoing fraud
                patterns to watch for.
              </p>
            </div>

            <div class="settings-page-block-content">
              <!-- Loading state -->
              <div
                v-if="isLoading"
                class="d-flex justify-content-center i-py-14"
              >
                <i-spinner></i-spinner>
              </div>

              <!-- Error state -->
              <div
                v-else-if="error && !isLoading"
                class="d-flex flex-column align-items-center i-py-14 i-gap-4"
              >
                <span class="i-text-body-2 i-text-error-90">{{ error }}</span>
              </div>

              <!-- Loaded content -->
              <div v-else class="d-flex flex-column i-gap-8">
                <div class="i-p-8 i-bg-neutral-20 i-radius-3xl">
                  <div class="d-flex align-items-center">
                    <i-icon
                      symbol="small-solid-info"
                      class="i-mr-3 i-text-neutral-110"
                      :size="16"
                    ></i-icon>
                    <span class="i-text-body-2 i-text-neutral-110">
                      Memory is specific to you in this workspace.
                    </span>
                  </div>
                </div>

                <i-text-input
                  id="workspace-memory"
                  :model-value="memory"
                  :mode="iTextInputModes.TEXTAREA"
                  :rows="8"
                  placeholder="Add information you'd like the Assistant to remember..."
                  @update:model-value="memory = $event"
                ></i-text-input>

                <div class="i-mt-4">
                  <i-button
                    text="Save"
                    size="lg"
                    data-testid="save-workspace-memory-button"
                    :disabled="!hasChanges || isSaving"
                    :loading="isSaving"
                    @button-click="save"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(#workspace-memory textarea) {
  padding-top: 12px;
  resize: vertical;
}
</style>
